// PRD.318 follow-up — treatment entry with optional Service linkage.
//
// One compact field that does both jobs the PRD asks for:
//
//     Treatment
//     [ Search services or enter treatment...        ]
//
// Picking a Service sets `serviceId` and SNAPSHOTS its current name as the
// label. Typing anything else leaves `serviceId` null and keeps the typed text.
// Nothing here creates a Service, and nothing fuzzy-matches free text against
// the catalogue — "Temporary dressing and reassess" stays exactly that.
//
// No billing side effects: selecting a Service records a catalogue reference
// and nothing more. No invoice, no line, no price, no charge.

import { useEffect, useMemo, useRef, useState } from 'react';
import { MicButton } from '../../../hooks/MicButton';
import { useVoiceToText } from '../../../hooks/useVoiceToText';
import {
  resolveTreatmentEntry,
  searchTreatmentServices,
  treatmentFromService,
  type TreatmentEntryValue,
  type TreatmentServiceOption,
} from '@helios/data-access';
import { NavIcon } from '../../../shell/shared';

export function TreatmentEntryInput({
  services,
  placeholder,
  primary,
  autoFocus,
  initialText = '',
  initialServiceId = null,
  voiceEnabled = false,
  onCommit,
  onCancel,
}: {
  services: readonly TreatmentServiceOption[];
  placeholder: string;
  primary: string;
  autoFocus?: boolean;
  /**
   * Clinic voice setting. A DRAFT row renders this component instead of
   * `HistoryEntryEditor`, so without a mic here dictation appeared to vanish
   * while adding an entry and only came back after it was saved.
   */
  voiceEnabled?: boolean;
  /**
   * Pre-filled label, used by Plan → Done so the clinician is not asked to
   * re-enter treatment they already documented.
   */
  initialText?: string;
  /** Service carried in with `initialText`. Preserved if that text is accepted. */
  initialServiceId?: string | null;
  /** Called with the resolved label + optional service reference. */
  onCommit: (value: TreatmentEntryValue) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialText);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the catalogue list when the click lands anywhere else. Matching
  // `ChipListField`: `mousedown`, not `click`, so the list is gone before a
  // control underneath reacts. Scoped to the wrapper, so the input, the mic and
  // the options themselves all count as inside and never self-close.
  //
  // Closing here only hides the list — it never commits, cancels or clears the
  // typed text. Clicking away from a half-typed treatment must not discard it.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Same pipeline and `translation` mode as HistoryEntryEditor, so dictating
  // into a draft row behaves exactly like dictating into a saved one.
  const vttEnabled = voiceEnabled && Boolean(import.meta.env.VITE_VOICE_TO_TEXT_URL);
  const vtt = useVoiceToText({
    mode: 'translation',
    onResult: (spoken) => {
      const clean = spoken.trim();
      if (!clean) return;
      // Append, never replace — dictation adds to whatever was typed, and the
      // service list re-filters on the combined text.
      setText((prev) => (prev.trim() ? `${prev.trim()} ${clean}` : clean));
      setOpen(true);
    },
  });
  // What the field was seeded with. Captured once so later edits compare
  // against the original, not against whatever is currently typed.
  const seeded = useRef({ text: initialText, serviceId: initialServiceId });

  const matches = useMemo(
    () => (open ? searchTreatmentServices(services, text) : []),
    [open, services, text],
  );

  const commitTyped = () => {
    // Accepting a seeded label unchanged KEEPS its service; editing it drops
    // the reference. See `resolveTreatmentEntry`.
    const value = resolveTreatmentEntry({
      text,
      seededText: seeded.current.text,
      seededServiceId: seeded.current.serviceId,
    });
    if (!value.treatment) return;
    onCommit(value);
    setText('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          value={text}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            // Enter commits whatever is typed as FREE TEXT. Choosing a Service
            // is always an explicit click, so a fast typist never gets a
            // catalogue reference they did not ask for.
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTyped();
            } else if (e.key === 'Escape') {
              setOpen(false);
              onCancel();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            boxSizing: 'border-box',
            padding: '6px 8px',
            // Room for the mic, which sits inside the field like every other
            // dictatable control.
            paddingRight: vttEnabled ? 34 : 8,
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${vtt.status === 'recording' ? primary : 'var(--border)'}`,
            outline: 'none',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-xs)',
            lineHeight: 'var(--line-height-body)',
            color: 'var(--text)',
            fontFamily: 'inherit',
          }}
        />
        {vttEnabled && (
          <MicButton
            positionStyle={{ top: 2, right: 34 }}
            status={vtt.status}
            primary={primary}
            elapsed={vtt.elapsed}
            maxDuration={vtt.maxDuration}
            onStart={() => vtt.start()}
            onStop={() => void vtt.stop()}
            onReset={() => vtt.reset()}
          />
        )}
        <button
          type="button"
          onClick={commitTyped}
          disabled={!text.trim()}
          title="Add"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: 'none',
            background: text.trim() ? primary : 'var(--text-disabled, #cbd5e1)',
            color: '#fff',
            cursor: text.trim() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
          }}
        >
          <NavIcon name="check" />
        </button>
      </div>

      {/* Catalogue matches. Presented as a choice, never auto-applied — a chart
          condition or diagnosis must never pick a Service on the clinician's
          behalf.

          Stays open when nothing matches. Free text is a first-class outcome
          here ("Temporary dressing and reassess"), so the panel that carries
          the "press Enter" hint must not vanish at exactly the moment the
          typed text has no catalogue equivalent — that reads as the search
          having broken rather than as no match. */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 20,
            marginTop: 4,
            minWidth: 240,
            maxWidth: 360,
            maxHeight: 320,
            overflowY: 'auto',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          }}
        >
          {matches.length === 0 && (
            <div
              style={{
                padding: '6px 10px',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              {text.trim() ? 'No matching service' : 'No services in the catalogue'}
            </div>
          )}
          {matches.map((svc) => (
            <button
              key={svc.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown, not click: the input's blur would otherwise close
                // the list before the selection registers.
                e.preventDefault();
                onCommit(treatmentFromService(svc));
                setText('');
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                border: 'none',
                background: 'transparent',
                color: 'var(--text)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {svc.name}
            </button>
          ))}
          <div
            style={{
              padding: '4px 10px 6px',
              fontSize: 'calc(var(--font-size-xs) * 0.85)',
              color: 'var(--text-placeholder, var(--text-muted))',
              borderTop: '1px solid var(--border)',
            }}
          >
            {/* "or" only makes sense as an alternative to something listed. */}
            {matches.length === 0
              ? 'Press Enter to enter it as free text'
              : 'or press Enter to enter manually'}
          </div>
        </div>
      )}
    </div>
  );
}
